import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";

const getStoredFilters = (filterType, defaultValue) => {
  const storedFilters = sessionStorage.getItem(filterType);
  return storedFilters ? JSON.parse(storedFilters) : defaultValue;
};

const useFilters = (location) => {
  const params = new URLSearchParams(location.search);
  const { municipality: pathMunicipality, type_of_room: pathTypeOfRoom } = useParams();

  // Initialize searchTerm with sessionStorage value FIRST
  const [searchTerm, setSearchTerm] = useState(() => {
    // Prioritera sessionStorage över URL parameter
    const savedSearchTerm = sessionStorage.getItem("vakansa-search-term");
    if (savedSearchTerm) {
      return savedSearchTerm;
    }
    // Fallback till URL parameter
    return params.get("q") || "";
  });

  const [selectedMunicipality, setSelectedMunicipality] = useState(() => {
    if (pathMunicipality) return pathMunicipality.split("-");
    return getStoredFilters("selectedMunicipality", []);
  });

  const [selectedRoomTypes, setSelectedRoomTypes] = useState(() => {
    if (pathTypeOfRoom) return pathTypeOfRoom.split("-");
    return getStoredFilters("selectedRoomTypes", []);
  });

  const [selectedTime, setSelectedTime] = useState(() => {
    return params.get("pristyp") ? params.get("pristyp").split(",") : getStoredFilters("selectedTime", []);
  });

  const [selectedPriceRange, setSelectedPriceRange] = useState(() => {
    const min = params.get("min-hyra");
    const max = params.get("max-hyra");
    if (min || max) {
      return {
        min: min ? parseInt(min, 10) : null,
        max: max ? parseInt(max, 10) : null,
      };
    }
    return getStoredFilters("selectedPriceRange", { min: null, max: null });
  });

  const [selectedAreaRange, setSelectedAreaRange] = useState(() => {
    const min = params.get("min-yta");
    const max = params.get("max-yta");
    if (min || max) {
      return {
        min: min ? parseInt(min, 10) : null,
        max: max ? parseInt(max, 10) : null,
      };
    }
    return getStoredFilters("selectedAreaRange", { min: null, max: null });
  });

  const [selectedMoms, setSelectedMoms] = useState(() => {
    const moms = params.get("moms");
    if (moms === "true") return ["true"]
    if (moms === "false") return ["false"];
    return getStoredFilters("selectedMoms", [])
  });

  const [selectedContractType, setSelectedContractType] = useState(() => {
    const contract = params.get("kontraktstyp");
    return contract ? [contract] : getStoredFilters("selectedContractType", []);
  });

  const [ selectedZones, setSelectedZones ] = useState(() => {
    const zones = params.get("zone");
    return zones ? zones.split(",") : getStoredFilters("selectedZones", []);
  });

  // Save searchTerm to sessionStorage whenever it changes
  useEffect(() => {
    if (searchTerm) {
      sessionStorage.setItem("vakansa-search-term", searchTerm);
    } else {
      sessionStorage.removeItem("vakansa-search-term");
    }
  }, [searchTerm]);

  // Save other filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("selectedTime", JSON.stringify(selectedTime));
    sessionStorage.setItem("selectedRoomTypes", JSON.stringify(selectedRoomTypes));
    sessionStorage.setItem("selectedMunicipality", JSON.stringify(selectedMunicipality));
    sessionStorage.setItem("selectedPriceRange", JSON.stringify(selectedPriceRange));
    sessionStorage.setItem("selectedAreaRange", JSON.stringify(selectedAreaRange));
    sessionStorage.setItem("selectedMoms", JSON.stringify(selectedMoms));
    sessionStorage.setItem("selectedContractType", JSON.stringify(selectedContractType));
    sessionStorage.setItem("selectedZones", JSON.stringify(selectedZones));
  }, [selectedTime, selectedRoomTypes, selectedMunicipality, selectedPriceRange, selectedAreaRange, selectedMoms, selectedContractType, selectedZones]);

  const resetFilters = useCallback(() => {
    setSelectedPriceRange({ min: null, max: null });
    setSelectedAreaRange({ min: null, max: null });
    setSelectedTime([]);
    setSelectedRoomTypes([]);
    setSelectedMunicipality([]);
    setSelectedMoms([]);
    setSelectedContractType([]);
    setSelectedZones([]);
    setSearchTerm("");
    // sessionStorage clearing happens automatically via useEffect
  }, []);

  const areFiltersActive = useCallback(() => {
    return (
      selectedMunicipality.length > 0 ||
      selectedRoomTypes.length > 0 ||
      selectedTime.length > 0 ||
      selectedAreaRange.min !== null ||
      selectedAreaRange.max !== null ||
      selectedPriceRange.min !== null ||
      selectedPriceRange.max !== null ||
      selectedMoms.length > 0 ||
      selectedContractType.length > 0 ||
      selectedZones.length > 0 ||
      searchTerm.trim().length > 0
    );
  }, [
    selectedMunicipality,
    selectedRoomTypes,
    selectedTime,
    selectedAreaRange,
    selectedPriceRange,
    selectedMoms,
    selectedContractType,
    selectedZones,
    searchTerm,
  ]);

  return {
    searchTerm,
    selectedMunicipality,
    selectedRoomTypes,
    selectedTime,
    selectedPriceRange,
    selectedAreaRange,
    selectedMoms,
    selectedContractType,
    selectedZones,
    setSearchTerm,
    setSelectedMunicipality,
    setSelectedRoomTypes,
    setSelectedTime,
    setSelectedPriceRange,
    setSelectedAreaRange,
    setSelectedMoms,
    setSelectedContractType,
    setSelectedZones,
    resetFilters,
    areFiltersActive,
  };
};

export default useFilters;
